import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Badge, Button, Card, PageHeader, Section, Stat } from '@/components/ui';
import { CUDA, MLX, PLATFORMS, formatGain, summaryGains } from '@/content/benchmarks';
import type { BenchmarkLevel } from '@/content/benchmarks';
import { SITE } from '@/lib/site';

/**
 * The benchmark overview. Carries no figures of its own: every result is the `summary` gain of a family in
 * `src/content/benchmarks.ts`, so the detail pages and this page cannot drift apart.
 */

const levels = [
  ['Direct inference', 'The Fnk engine called directly, against another inference server on the same hardware and model. No backend, no framework — the engine alone.'],
  ['End to end', 'Gatling chat traffic into a backend running the AI framework, which calls inference. The Fnk AI stack against the stack a team assembles today.'],
];

function LevelSummary({ level }: { level: BenchmarkLevel }) {
  const gains = summaryGains(level);
  return (
    <div>
      <p className="label mb-2">{level.title}</p>
      <table className="w-full border-collapse text-left">
        <tbody>
          {gains.map((g) => (
            <tr key={`${g.metric}-${g.vs}`} className="border-b border-line last:border-b-0">
              <td className="py-2 pr-4 align-top text-[13px] text-ink">
                {g.metric}
                <span className="block text-[12px] text-ink-3">vs {g.vs}</span>
              </td>
              <td className="whitespace-nowrap py-2 text-right align-top font-mono text-[13px] font-semibold tabular text-accent-ink">{formatGain(g)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BenchmarkPage() {
  const cudaTop = summaryGains(CUDA.e2e)[0];
  const cudaEngine = summaryGains(CUDA.direct)[0];
  const mlxTop = summaryGains(MLX.e2e)[0];

  return (
    <div>
      <PageHeader
        kicker="Benchmark"
        title={<span>Measured on two accelerator families, at the engine and end to end.</span>}
        lead="Fnk is measured on NVIDIA CUDA and on Apple Silicon (MLX). Each family is tested at two levels: the engine called directly against another inference server, and the whole stack — AI framework in front of inference — against the stack a customer runs today. Every result is relative to a named baseline."
        actions={
          <>
            <Link to={CUDA.path} className="w-full sm:w-auto">
              <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="h-4 w-4" />}>NVIDIA CUDA results</Button>
            </Link>
            <Link to={MLX.path} className="w-full sm:w-auto">
              <Button variant="secondary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="h-4 w-4" />}>Apple Silicon / MLX results</Button>
            </Link>
          </>
        }
      />

      <section className="border-b border-line bg-paper-2">
        <div className="mx-auto grid max-w-site grid-cols-1 gap-6 px-4 py-10 sm:grid-cols-3 sm:px-6">
          <Stat value={formatGain(cudaTop)} label={`${cudaTop.metric}, NVIDIA CUDA end to end — vs ${cudaTop.vs}`} tone="accent" />
          <Stat value={formatGain(mlxTop)} label={`${mlxTop.metric}, Apple Silicon end to end — vs ${mlxTop.vs}`} />
          <Stat value={formatGain(cudaEngine)} label={`${cudaEngine.metric}, Fnk engine on NVIDIA — vs ${cudaEngine.vs}`} />
        </div>
      </section>

      <Section n="01" kicker="Two levels" title="Every family is measured twice: the engine alone, then the whole stack.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {levels.map(([t, d]) => (
            <Card key={t} variant="plain" className="p-5">
              <h3 className="text-sm font-semibold tracking-tight">{t}</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2">{d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section n="02" kicker="Summary" title="The same direction on both families." tone="white">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {PLATFORMS.map((p) => (
            <Card key={p.slug} variant="raised" className="flex flex-col p-5 sm:p-6">
              <div>
                <Badge variant="accent">{p.name}</Badge>
              </div>
              <p className="mt-3 text-[12px] leading-relaxed text-ink-3">
                {p.model} · {p.accelerator}
              </p>
              <div className="mt-5 flex-1 space-y-6">
                <LevelSummary level={p.direct} />
                <LevelSummary level={p.e2e} />
              </div>
              <Link to={p.path} className="mt-6 w-full sm:w-auto">
                <Button variant="secondary" className="w-full sm:w-auto" rightIcon={<ArrowRight className="h-4 w-4" />}>Full {p.name} results and method</Button>
              </Link>
            </Card>
          ))}
        </div>
        <p className="mt-6 max-w-3xl text-sm leading-relaxed text-ink-2">
          The two families run a different model, runtime and accelerator, so they are never compared on the numbers. What repeats is the direction: the
          framework makes the backend lighter, and the large gains appear once admission, capacity signalling and cancellation continue through the
          inference boundary into the engine.
        </p>
      </Section>

      <Section n="03" kicker="What the customer buys from this" title="Fewer servers, predictable overload, a known unit cost." tone="white">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Fewer servers for the same work', 'More completed work from the hardware you already run, in favourable workloads.'],
            ['Predictable behaviour under load', 'Bounded overload with a stated reason, not global degradation.'],
            ['A known cost per business action', 'A number a CFO can approve, not a token bill nobody can attribute.'],
            ['The next purchase sized on real load', 'Replay-based sizing, not a peak guess.'],
          ].map(([t, d]) => (
            <Card key={t} variant="plain" className="p-5">
              <h3 className="text-sm font-semibold tracking-tight">{t}</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2">{d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <section className="border-t border-line">
        <div className="mx-auto max-w-site px-4 py-14 sm:px-6 md:py-20">
          <Card variant="accent" className="p-6 sm:p-8 md:p-10">
            <h2 className="text-section-mobile font-semibold tracking-heading md:text-section-desktop">The number that matters is the one on your workload.</h2>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink-2">
              An assessment replays your recorded load on the Fnk stack against the one you run today, and reports the difference — the number that matters is
              the one on your workload, not on this page.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a href={SITE.assessmentMailto} className="w-full sm:w-auto">
                <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowUpRight className="h-4 w-4" />}>Request an assessment</Button>
              </a>
              <Link to="/pricing" className="w-full sm:w-auto">
                <Button variant="ghost" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="h-4 w-4" />}>Pricing</Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}
